import { useState, useMemo } from "react";
import { Receipt, Plus, Package, Filter, Search, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCommands, CommandStatus } from "@/hooks/useCommands";
import { CommandFormDialog } from "@/components/commands/CommandFormDialog";
import { CommandCard } from "@/components/commands/CommandCard";
import { BatchCommandDialog } from "@/components/commands/BatchCommandDialog";
import { cn } from "@/lib/utils";

type StatusFilter = "all" | CommandStatus;
type SortOption = "number" | "status" | "updated";

const ITEMS_PER_PAGE = 50;

const statusFilters: { value: StatusFilter; label: string; color: string }[] = [
  { value: "all", label: "Todas", color: "bg-muted hover:bg-muted/80" },
  { value: "available", label: "Livres", color: "bg-emerald-500 hover:bg-emerald-600" },
  { value: "in_use", label: "Em uso", color: "bg-amber-500 hover:bg-amber-600" },
];

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "number", label: "Número" },
  { value: "status", label: "Status" },
  { value: "updated", label: "Última atualização" },
];

const statusOrder: Record<string, number> = {
  in_use: 0,
  available: 1,
};

export default function ComandasPage() {
  const { commands, isLoading, createCommand, createBatchCommands, updateCommand, deleteCommand } = useCommands();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [editingCommand, setEditingCommand] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("number");
  const [currentPage, setCurrentPage] = useState(1);

  const handleCreate = async (data: { number: string }) => {
    const result = await createCommand(data);
    if (result) setDialogOpen(false);
  };

  const handleBatchCreate = async (start: number, end: number) => {
    const result = await createBatchCommands(start, end);
    if (result) setBatchDialogOpen(false);
  };

  const handleEdit = (command: any) => {
    setEditingCommand(command);
    setDialogOpen(true);
  };

  const handleUpdate = async (data: { number: string }) => {
    if (!editingCommand) return;
    const result = await updateCommand(editingCommand.id, data);
    if (result) {
      setDialogOpen(false);
      setEditingCommand(null);
    }
  };

  const handleToggleActive = async (command: any) => {
    await updateCommand(command.id, { is_active: !command.is_active });
  };

  const handleDelete = async (id: string) => {
    await deleteCommand(id);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCommand(null);
  };

  const activeCommands = useMemo(() => commands.filter((c) => c.is_active), [commands]);
  const inactiveCommands = useMemo(() => commands.filter((c) => !c.is_active), [commands]);

  const filteredActiveCommands = useMemo(() => {
    let filtered = activeCommands;
    
    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter((c) => 
        c.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "number":
          return a.number.localeCompare(b.number, undefined, { numeric: true });
        case "status":
          return (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
        case "updated":
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [activeCommands, statusFilter, searchQuery, sortBy]);

  // Reset page when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredActiveCommands.length / ITEMS_PER_PAGE);
  const paginatedCommands = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredActiveCommands.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredActiveCommands, currentPage]);

  // Count by status
  const statusCounts = useMemo(() => ({
    all: activeCommands.length,
    available: activeCommands.filter((c) => c.status === "available").length,
    in_use: activeCommands.filter((c) => c.status === "in_use").length,
  }), [activeCommands]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
            <Receipt className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Comandas</h1>
            <p className="text-sm text-muted-foreground">Gerencie as comandas do restaurante</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setBatchDialogOpen(true)}>
            <Package className="h-4 w-4" />
            Criar em Lote
          </Button>
          <Button className="gap-2" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Nova Comanda
          </Button>
        </div>
      </div>

      {/* Search, Sort and Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar comanda..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {statusFilters.map((filter) => (
            <Badge
              key={filter.value}
              variant={statusFilter === filter.value ? "default" : "outline"}
              className={cn(
                "cursor-pointer transition-all",
                statusFilter === filter.value 
                  ? filter.value === "all" 
                    ? "bg-foreground text-background" 
                    : `${filter.color} text-white border-transparent`
                  : "hover:bg-muted"
              )}
              onClick={() => setStatusFilter(filter.value)}
            >
              {filter.label} ({statusCounts[filter.value]})
            </Badge>
          ))}
        </div>
      </div>

      {/* Active Commands */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          {statusFilter === "all" 
            ? `Comandas Ativas (${filteredActiveCommands.length})`
            : `${statusFilters.find(f => f.value === statusFilter)?.label} (${filteredActiveCommands.length})`
          }
        </h2>
        {filteredActiveCommands.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                {statusFilter === "all" 
                  ? "Nenhuma comanda cadastrada ainda.\nClique em \"Nova Comanda\" ou \"Criar em Lote\" para começar."
                  : `Nenhuma comanda ${statusFilters.find(f => f.value === statusFilter)?.label.toLowerCase()}.`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
              {paginatedCommands.map((command) => (
                <CommandCard
                  key={command.id}
                  command={command}
                  onEdit={() => handleEdit(command)}
                  onToggleActive={() => handleToggleActive(command)}
                  onDelete={() => handleDelete(command.id)}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground ml-2">
                  {filteredActiveCommands.length} comandas
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Inactive Commands */}
      {inactiveCommands.length > 0 && statusFilter === "all" && (
        <div>
          <h2 className="text-lg font-semibold mb-4 text-muted-foreground">
            Comandas Inativas ({inactiveCommands.length})
          </h2>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
            {inactiveCommands.map((command) => (
              <CommandCard
                key={command.id}
                command={command}
                onEdit={() => handleEdit(command)}
                onToggleActive={() => handleToggleActive(command)}
                onDelete={() => handleDelete(command.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Form Dialogs */}
      <CommandFormDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={editingCommand ? handleUpdate : handleCreate}
        initialData={editingCommand}
      />

      <BatchCommandDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        onSubmit={handleBatchCreate}
      />
    </div>
  );
}
